import dynamic from 'next/dynamic'
import { STACKED_BAR_CHART_COLORS } from '@/utils/analytics'
import { formatSnakeStringWithCapitalization } from '../../utils/string_utils'
import { processSingleDateLabelFull } from './time_bucket_processing'
import { StatsDateBucketInterval } from '@/generated/enums'
import { Text } from '@chakra-ui/react'
import { DOPPEL_GREY } from '@/utils/style'
import {
  dateToStringKey,
  getXAxisTickValues,
  makeLegendData,
  makeTimeLabelMap,
  TimeSeriesChartData,
} from './time_series_utils'
import StatsTooltip from './stats_tooltip'
import { LazyLoadState } from './dashboard/dashboard_utils'

type TimeSeriesBarChartProps<K extends string> = {
  chartData: TimeSeriesChartData<K> | null
  legendKeys: K[]
  bucketInterval: StatsDateBucketInterval
  xAxisTitle?: string
  yAxisLabels?: number[]
  yAxisTitle?: string
  barColors?: string[] // TODO: temporary props to not impact current view
  legendPosition?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  margin?: { top?: number; right?: number; bottom?: number; left?: number }
  setLoading?: (loading: boolean) => void
}

const TimeSeriesBarChart = <K extends string>({
  chartData,
  legendKeys,
  bucketInterval,
  xAxisTitle = null,
  yAxisLabels = undefined,
  yAxisTitle = null,
  barColors = STACKED_BAR_CHART_COLORS,
  legendPosition = 'bottom-right',
  margin = {},
  setLoading = () => {},
}: TimeSeriesBarChartProps<K>) => {
  const indexBy = 'date'

  const timeLabelMap = makeTimeLabelMap(chartData, bucketInterval)

  const ResponsiveBar = dynamic(
    async () => {
      const mod = await import('@nivo/bar')
      return mod.ResponsiveBar
    },
    {
      loading: () => <LazyLoadState setLoading={setLoading} />,
    },
  )

  const additionalYAxisLabelProps = yAxisLabels
    ? {
        gridYValues: yAxisLabels,
        maxValue: yAxisLabels[yAxisLabels.length - 1],
      }
    : {}

  if (!chartData) return null

  return (
    <ResponsiveBar
      axisBottom={{
        tickValues: getXAxisTickValues(chartData, bucketInterval).map((date) =>
          dateToStringKey(date, bucketInterval),
        ),
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,

        legend: xAxisTitle,
        legendPosition: 'middle',
        legendOffset: 32,
        format: (value) => timeLabelMap[value] || value,
      }}
      axisLeft={{
        tickValues: yAxisLabels,
        tickSize: 2,
        tickPadding: 5,
        tickRotation: 0,
        legend: yAxisTitle,
        legendPosition: 'middle',
        legendOffset: -40,
      }}
      axisRight={null}
      axisTop={null}
      borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
      colors={({ id }) => barColors[legendKeys.indexOf(id as K)]}
      data={chartData.map((item) => ({
        date: dateToStringKey(item.date, bucketInterval),
        ...item.values,
      }))}
      enableLabel={false}
      indexBy={indexBy}
      indexScale={{ type: 'band', round: true }}
      keys={legendKeys}
      labelSkipHeight={12}
      labelSkipWidth={12}
      legends={[
        {
          dataFrom: 'keys',
          anchor: legendPosition,
          direction: 'column',
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 2,
          itemWidth: 100,
          itemHeight: 20,
          itemDirection: 'left-to-right',
          itemOpacity: 0.85,
          symbolSize: 20,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1,
              },
            },
          ],
          data: makeLegendData(legendKeys, barColors),
        },
      ]}
      margin={margin}
      padding={0.3}
      theme={{
        text: {
          fill: DOPPEL_GREY,
        },
        grid: {
          line: {
            stroke: DOPPEL_GREY,
            strokeWidth: 0.5, // Set the stroke width of the gridY lines
            strokeOpacity: 0.2,
          },
        },
      }}
      tooltip={({ id, value, indexValue }) => (
        <StatsTooltip>
          <Text>{processSingleDateLabelFull(`${indexValue}`, bucketInterval)}</Text>

          <Text>
            {formatSnakeStringWithCapitalization(String(id))}:{' '}
            <Text as="b">{value}</Text>
          </Text>
        </StatsTooltip>
      )}
      valueScale={{ type: 'linear' }}
      {...additionalYAxisLabelProps}
    />
  )
}

export default TimeSeriesBarChart
